(() => {
  const LOADING = 'data-image-loading';
  const LOADED = 'data-image-loaded';
  const ERROR = 'data-image-error';
  const IGNORE = 'data-no-image-loading';
  const OBSERVED = 'data-image-loading-observed';
  const SMALL_IMAGE_LIMIT = 40;

  const imageSelector = 'img:not([data-no-image-loading]):not([loading="eager"]):not([fetchpriority="high"])';

  function getImageSource(img) {
    return img.currentSrc || img.getAttribute('src') || img.getAttribute('srcset') || '';
  }

  function isSvgImage(img) {
    return /\.svg(?:[?#].*)?$/i.test(getImageSource(img).split(' ')[0]);
  }

  function isSmallImage(img) {
    const width = Number.parseInt(img.getAttribute('width') || '', 10);
    const height = Number.parseInt(img.getAttribute('height') || '', 10);

    return width > 0 && height > 0 && width <= SMALL_IMAGE_LIMIT && height <= SMALL_IMAGE_LIMIT;
  }

  function shouldShowLoadingEffect(img) {
    return !img.hasAttribute(IGNORE) && !isSvgImage(img) && !isSmallImage(img);
  }

  function prepareImage(img) {
    if (
      !(img instanceof HTMLImageElement) ||
      img.hasAttribute(IGNORE) ||
      img.closest('dialog:not([open]), template')
    ) {
      return;
    }

    const loading = img.getAttribute('loading');

    if (!loading || loading === 'auto') {
      img.loading = 'lazy';
    }

    if (!img.hasAttribute('decoding')) {
      img.decoding = 'async';
    }
  }

  function clearState(img) {
    img.removeAttribute(LOADING);
    img.removeAttribute(LOADED);
    img.removeAttribute(ERROR);
  }

  function markLoaded(img) {
    img.removeAttribute(LOADING);
    img.removeAttribute(ERROR);
    img.setAttribute(LOADED, 'true');
  }

  function markError(img) {
    img.removeAttribute(LOADING);
    img.removeAttribute(LOADED);
    img.setAttribute(ERROR, 'true');
  }

  function markLoading(img) {
    img.removeAttribute(LOADED);
    img.removeAttribute(ERROR);

    if (shouldShowLoadingEffect(img)) {
      img.setAttribute(LOADING, 'true');
    } else {
      img.removeAttribute(LOADING);
    }
  }

  function syncImageState(img) {
    if (img.closest('dialog:not([open]), template')) return;

    prepareImage(img);

    const hasSource = Boolean(getImageSource(img));

    if (!hasSource) {
      clearState(img);
      return;
    }

    if (img.complete) {
      if (img.naturalWidth > 0) {
        markLoaded(img);
      } else {
        markError(img);
      }

      return;
    }

    markLoading(img);
  }

  function observeImage(img) {
    if (!(img instanceof HTMLImageElement) || img.hasAttribute(OBSERVED)) return;

    img.setAttribute(OBSERVED, 'true');
    img.addEventListener('load', () => markLoaded(img));
    img.addEventListener('error', () => markError(img));
    syncImageState(img);
  }

  function observeImages(root = document) {
    if (root instanceof HTMLImageElement) {
      observeImage(root);
      return;
    }

    if (!(root instanceof Document) && !(root instanceof Element)) return;

    root.querySelectorAll(imageSelector).forEach(observeImage);
  }

  observeImages();

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        for (const node of mutation.addedNodes) {
          if (node instanceof Element) observeImages(node);
        }

        continue;
      }

      if (mutation.type === 'attributes' && mutation.target instanceof HTMLImageElement) {
        syncImageState(mutation.target);
      }
    }
  });

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['src', 'srcset', 'sizes'],
    childList: true,
    subtree: true,
  });
})();
